import React from 'react'
import s from './Accordion.module.scss'
import cn from 'classnames'
import { IconClose, IconOpen } from '../Icons'

interface RenderProps {
    isOpen: boolean
}

interface Content {
    id: string
    Title: ({ isOpen }: RenderProps) => React.ReactElement
    Content: ({ isOpen }: RenderProps) => React.ReactElement
    buttonStyles?: ({ isOpen }: RenderProps) => React.CSSProperties
    buttonClass?: ({ isOpen }: RenderProps) => string
    itemClassName?: string
    initialState?: boolean
}

interface Props {
    contents: Array<Content> | null
    OpenIcon?: React.ReactNode
    CloseIcon?: React.ReactNode
    className?: string
}

function AccordionItem({
    item,
    OpenIcon,
    CloseIcon
}: {
    item: Content
    OpenIcon: React.ReactNode
    CloseIcon: React.ReactNode
}) {
    const [isOpen, setIsOpen] = React.useState(item?.initialState || false)
    const contentEl = React.useRef<HTMLDivElement>(null)

    const { Title, Content } = item

    const handleToggle = () => {
        setIsOpen((prev) => !prev)
    }

    return (
        <div
            className={cn(
                s['accordion-item-container'],
                { 'pb-4': isOpen },
                item.itemClassName
            )}
        >
            <button
                onClick={handleToggle}
                type="button"
                className={cn(s['button'], item.buttonClass?.({ isOpen }))}
                style={item.buttonStyles?.({ isOpen })}
            >
                <Title isOpen={isOpen} />
                {isOpen ? CloseIcon : OpenIcon}
            </button>
            <div
                style={
                    isOpen
                        ? { height: contentEl.current?.scrollHeight }
                        : { height: '0' }
                }
                className={s['content-container']}
                role="contentinfo"
                ref={contentEl}
                aria-hidden={!isOpen}
            >
                <Content isOpen={isOpen} />
            </div>
        </div>
    )
}

function Accordion({
    contents,
    OpenIcon = <IconOpen />,
    CloseIcon = <IconClose />,
    className
}: Props): React.ReactElement | null {
    if (!contents) return null
    return (
        <div className={cn(s['accordion-container'], className)}>
            {contents.map((content) => (
                <AccordionItem
                    key={content.id}
                    item={content}
                    OpenIcon={OpenIcon}
                    CloseIcon={CloseIcon}
                />
            ))}
        </div>
    )
}

export default Accordion
